import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import QuoteBuilder from "@/components/ponude/QuoteBuilder";
import PricingAdmin from "@/components/ponude/PricingAdmin";
import QuotesList from "@/components/ponude/QuotesList";

const API = process.env.REACT_APP_BACKEND_URL;

const MODEL_IMAGES = {
    lunar: "https://www.smart-mak.com/wp-content/uploads/2026/02/aluminium-lamellenzaun-lunar-700-800px.png",
    stellar: "https://www.smart-mak.com/wp-content/uploads/2026/02/aluminium-zaun-mit-breiten-lamellen-stellar-700-800px-1.png",
    astral: "https://www.smart-mak.com/wp-content/uploads/2026/02/aluminium-zaun-astral-mit-zeitloser-vertikaler-linienfuehrung-700-800px.png",
    eclipse: "https://www.smart-mak.com/wp-content/uploads/2026/02/aluminium-sichtschutzzaun-eclipse-700-800px.png",
    orbit: "https://www.smart-mak.com/wp-content/uploads/2026/02/aluminium-premium-zaunsystem-orbit-mit-waagerechten-lamellen-700-800px.png",
    galactic: "https://www.smart-mak.com/wp-content/uploads/2026/02/aluminium-premium-zaunsystem-mit-vertikaler-linienfuehrung-galactic-700-800px.png",
    infinity: "https://customer-assets-lxgj4vgw.emergentagent.net/job_a708df4e-9925-43fe-a63c-ae2453af62f8/artifacts/f9rfg4he_infinity-700-800px.png",
};

export default function PonudeAppPage() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("nova");
    const [pricing, setPricing] = useState({ models: [], gates: [], motors: [], accessories: [] });
    const [pricingLoading, setPricingLoading] = useState(true);
    const [reuseQuote, setReuseQuote] = useState(null);

    useEffect(() => {
        document.title = "MAK · Generator ponuda";
        if (!loading && !user) navigate("/ponude/login", { replace: true });
    }, [user, loading, navigate]);

    const loadPricing = async () => {
        setPricingLoading(true);
        try {
            const [m, g, mo, a] = await Promise.all([
                fetch(`${API}/api/pricing/models`, { credentials: "include" }).then(r => r.json()),
                fetch(`${API}/api/pricing/gates`, { credentials: "include" }).then(r => r.json()),
                fetch(`${API}/api/pricing/motors`, { credentials: "include" }).then(r => r.json()),
                fetch(`${API}/api/pricing/accessories`, { credentials: "include" }).then(r => r.json()),
            ]);
            setPricing({ models: m, gates: g, motors: mo, accessories: a });
        } catch (e) {
            toast.error("Greška pri učitavanju cjenovnika");
        } finally {
            setPricingLoading(false);
        }
    };

    useEffect(() => {
        if (user) loadPricing();
    }, [user]);

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--paper-2))]">
                <div className="w-10 h-10 border-4 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(var(--paper-2))]">
            {/* Top bar */}
            <header className="bg-white border-b border-[hsl(var(--line))]">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="font-bold tracking-widest text-sm text-[hsl(var(--accent))]">ALU-OGRADE.BA</span>
                        <span className="text-[hsl(var(--muted-ink))] text-xs hidden sm:inline">· Generator ponuda</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                            <div className="text-xs font-medium" data-testid="user-name">{user.name}</div>
                            <div className="text-[10px] text-[hsl(var(--muted-ink))]">{user.email}</div>
                        </div>
                        {user.picture && (
                            <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-[hsl(var(--line))]" />
                        )}
                        <button
                            onClick={async () => { await logout(); navigate("/ponude/login"); }}
                            data-testid="logout-btn"
                            className="text-xs text-[hsl(var(--muted-ink))] hover:text-[hsl(var(--ink))] underline-offset-4 hover:underline"
                        >
                            Odjava
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="max-w-7xl mx-auto px-6 flex gap-1 border-t border-[hsl(var(--line))]">
                    <TabBtn active={tab === "nova"} onClick={() => setTab("nova")} testid="tab-nova">
                        Nova ponuda
                    </TabBtn>
                    <TabBtn active={tab === "ponude"} onClick={() => setTab("ponude")} testid="tab-ponude">
                        Ponude
                    </TabBtn>
                    <TabBtn active={tab === "cjenovnik"} onClick={() => setTab("cjenovnik")} testid="tab-cjenovnik">
                        Cjenovnik
                    </TabBtn>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {pricingLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="w-8 h-8 border-4 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : tab === "nova" ? (
                    <QuoteBuilder pricing={pricing} modelImages={MODEL_IMAGES} initial={reuseQuote} key={reuseQuote?.quote_id || "blank"} />
                ) : tab === "ponude" ? (
                    <QuotesList onReuse={(q) => { setReuseQuote(q); setTab("nova"); }} />
                ) : (
                    <PricingAdmin pricing={pricing} reload={loadPricing} />
                )}
            </main>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                active
                    ? "border-[hsl(var(--accent))] text-[hsl(var(--ink))]"
                    : "border-transparent text-[hsl(var(--muted-ink))] hover:text-[hsl(var(--ink))]"
            }`}
        >
            {children}
        </button>
    );
}
